import {
  getMcpTools,
  getResolverServices,
  type ProjectSchemaJSON,
  type QueryJSON,
  type RuntimeSchema,
  type UserSchema
} from '@takeshape/schema';
import type {
  SchemaIndex,
  SchemaIndexEntryData,
  SchemaIndexEntryMutationData,
  SchemaIndexEntryQueryData,
  SchemaIndexEntryShapeData
} from 'app/schema/types';
import compact from 'lodash/compact';
import { getShapeQueryName } from '../../api-explorer/util/get-query';
import { groupByShape } from '../../common/utils/group-by-shape';
import { showInDataTab } from '../../common/utils/shape-predicates';
import { SchemaView } from '../slice';
import { getShapeServices } from './get-shape-services';

const typeOrder = ['shape', 'query', 'mutation'];

const makeTooltipServicesString = (schema: ProjectSchemaJSON, services: string[]) => {
  return compact(
    services.map((service) => (service === 'shapedb' ? 'ShapeDB' : schema.services?.[service]?.title))
  ).join(', ');
};

type CreateDataArgs = {
  schema: ProjectSchemaJSON;
  name: string;
  shapeName?: string;
  object: QueryJSON;
  grouped: boolean;
  topLevelGroup?: boolean;
  isCollapsed: boolean;
  shapeQueriesAndMutations?: Pick<ProjectSchemaJSON, 'queries' | 'mutations'>;
};

const createQueryData = (args: CreateDataArgs): SchemaIndexEntryQueryData => {
  const { schema, name, shapeName, object, grouped, topLevelGroup, isCollapsed, shapeQueriesAndMutations } = args;
  const services = getResolverServices(object.resolver);
  return {
    name,
    shapeName,
    type: 'query',
    services,
    tooltipServiceString: makeTooltipServicesString(schema, services),
    showDataActions: false,
    disabled: shapeQueriesAndMutations ? shapeQueriesAndMutations.queries[name] !== undefined : false,
    object,
    grouped,
    topLevelGroup,
    isCollapsed
  };
};

const createMutationData = (args: CreateDataArgs): SchemaIndexEntryMutationData => {
  const { schema, name, shapeName, object, grouped, topLevelGroup, isCollapsed, shapeQueriesAndMutations } = args;
  const services = getResolverServices(object.resolver);
  return {
    name,
    shapeName,
    type: 'mutation',
    services,
    tooltipServiceString: makeTooltipServicesString(schema, services),
    showDataActions: false,
    disabled: shapeQueriesAndMutations ? shapeQueriesAndMutations.mutations[name] !== undefined : false,
    object,
    grouped,
    topLevelGroup,
    isCollapsed
  };
};

type BuildSchemaIndexArgs = {
  userSchema: UserSchema;
  view: SchemaView;
  shapeQueriesAndMutations?: Pick<ProjectSchemaJSON, 'queries' | 'mutations'>;
  collapsed?: string[];
  runtimeSchema?: RuntimeSchema;
};

export function buildSchemaIndex(args: BuildSchemaIndexArgs): SchemaIndex {
  const { userSchema, view, shapeQueriesAndMutations, collapsed = [], runtimeSchema } = args;
  const schema = userSchema.json;
  const groups = view === SchemaView.Grouped ? groupByShape(schema) : null;
  const { shapes, queries, mutations } = schema;
  const index: SchemaIndex = [];

  const agents = userSchema.getAgents();
  if (agents.length) {
    for (const agent of agents.sort((a, b) => a.name.localeCompare(b.name))) {
      index.push({
        name: agent.name,
        type: 'agent',
        object: agent.json,
        services: [],
        showDataActions: false,
        disabled: false,
        grouped: Boolean(groups),
        topLevelGroup: true
      });
      if (runtimeSchema) {
        const agentMutations = runtimeSchema.getAgent(agent.name)?.getMutations();
        if (agentMutations) {
          index.push(
            ...agentMutations.map(
              (mutation): SchemaIndexEntryData => ({
                name: mutation.name,
                type: 'mutation',
                object: mutation.json,
                services: [],
                showDataActions: false,
                showEdit: false,
                disabled: false,
                grouped: true
              })
            )
          );
        }

        const agentQueries = runtimeSchema.getAgent(agent.name)?.getQueries();
        if (agentQueries?.length) {
          index.push(
            ...agentQueries.map(
              (query): SchemaIndexEntryData => ({
                name: query.name,
                type: 'query',
                object: query.json,
                services: [],
                showDataActions: false,
                showEdit: false,
                disabled: false,
                grouped: true
              })
            )
          );
        }
      }
    }
  }

  const tools = getMcpTools(schema);
  if (tools) {
    for (const tool of tools.sort((a, b) => a.name.localeCompare(b.name))) {
      index.push({
        name: tool.name,
        type: 'tool',
        object: tool,
        services: [],
        showDataActions: false,
        disabled: false,
        grouped: Boolean(groups),
        topLevelGroup: true
      });
    }
  }

  for (const [shapeName, shape] of Object.entries(shapes).sort(([a], [b]) => a.localeCompare(b))) {
    const isCollapsed = view === SchemaView.Grouped && collapsed.includes(shapeName);
    const services = getShapeServices(schema, shape);
    const currentShapeEntry: SchemaIndexEntryShapeData = {
      name: shapeName,
      type: 'shape',
      services,
      tooltipServiceString: makeTooltipServicesString(schema, services),
      showDataActions: showInDataTab(shape),
      previewQuery: getShapeQueryName(schema, shape.name),
      disabled: false,
      object: shape,
      grouped: Boolean(groups),
      showCollapseButton: view === SchemaView.Grouped,
      isCollapsed
    };
    index.push(currentShapeEntry);

    let hasNested = false;
    if (groups) {
      if (groups.shapes[shapeName]) {
        const queries = groups.shapes[shapeName].queries ?? [];
        for (const [queryName, query] of Object.entries(queries)) {
          hasNested = true;
          index.push(
            createQueryData({
              schema,
              name: queryName,
              shapeName,
              object: query,
              grouped: true,
              isCollapsed,
              shapeQueriesAndMutations
            })
          );
        }

        const mutations = groups.shapes[shapeName].mutations ?? [];
        for (const [mutationName, mutation] of Object.entries(mutations)) {
          hasNested = true;
          index.push(
            createMutationData({
              schema,
              name: mutationName,
              shapeName,
              object: mutation,
              grouped: true,
              isCollapsed,
              shapeQueriesAndMutations
            })
          );
        }
      }
    }

    if (!hasNested) {
      currentShapeEntry.showCollapseButton = false;
    }
  }

  if (groups) {
    const queries = groups.queries ?? [];
    for (const [queryName, query] of Object.entries(queries).sort((a, b) => (a[0].localeCompare(b[0]) ? 1 : -1))) {
      index.push(
        createQueryData({
          schema,
          name: queryName,
          object: query,
          grouped: true,
          topLevelGroup: true,
          isCollapsed: false,
          shapeQueriesAndMutations
        })
      );
    }

    const mutations = groups.mutations ?? [];
    for (const [mutationName, mutation] of Object.entries(mutations).sort((a, b) =>
      a[0].localeCompare(b[0]) ? 1 : -1
    )) {
      index.push(
        createMutationData({
          schema,
          name: mutationName,
          object: mutation,
          grouped: true,
          topLevelGroup: true,
          isCollapsed: false,
          shapeQueriesAndMutations
        })
      );
    }
  }

  if (!groups) {
    for (const [queryName, query] of Object.entries(queries)) {
      index.push(
        createQueryData({
          schema,
          name: queryName,
          object: query,
          grouped: false,
          isCollapsed: false,
          shapeQueriesAndMutations
        })
      );
    }
    for (const [mutationName, mutation] of Object.entries(mutations)) {
      index.push(
        createMutationData({
          schema,
          name: mutationName,
          object: mutation,
          grouped: false,
          isCollapsed: false,
          shapeQueriesAndMutations
        })
      );
    }
    return (index as SchemaIndexEntryData[]).sort((a, b) => {
      if (a.type === b.type) {
        return a.name.localeCompare(b.name);
      }

      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
  }

  return index;
}

export default buildSchemaIndex;
